import { ChangeDetectionStrategy, Component, input } from '@angular/core';

import { CoverageSummary, FulfillmentSummary } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { ChipComponent } from './chip.component';

/**
 * How a set of prescriptions stands, in the slot a plain count used to occupy: the ruling
 * breakdown, what stopped, and how much was actually delivered.
 *
 * The same block renders for a whole guarantee and for one diagnosis group, so the numbers under
 * an open card are read the same way as the ones on its header.
 */
@Component({
  selector: 'guarantees-prescription-stats',
  standalone: true,
  imports: [TranslatePipe, ChipComponent],
  template: `
    <span class="inline-flex shrink-0 items-center gap-1.5">
      <!-- Zero buckets are dropped: colour identifies each number, so an empty slot only costs width. -->
      <guarantees-chip
        icon="list_alt"
        [tooltip]="
          'guaranteeV2.message.coverageSummaryHint'
            | translate: { total: summary().total, covered: summary().covered, notCovered: summary().notCovered, undecided: summary().undecided }
        ">
        @if (summary().total === 0) {
          <span class="text-neutral-500">0</span>
        } @else {
          @if (summary().covered > 0) {
            <span class="text-success-700">{{ summary().covered }}</span>
          }
          @if (summary().notCovered > 0) {
            @if (summary().covered > 0) {
              <span class="text-neutral-300">/</span>
            }
            <span class="text-error-700">{{ summary().notCovered }}</span>
          }
          @if (summary().undecided > 0) {
            @if (summary().covered > 0 || summary().notCovered > 0) {
              <span class="text-neutral-300">/</span>
            }
            <span class="text-warning-800">{{ summary().undecided }}</span>
          }
        }
      </guarantees-chip>

      @if (fulfillment().stopped > 0) {
        <guarantees-chip tone="error" variant="outline" icon="cancel" [tooltip]="'guaranteeV2.message.stoppedLinesHint' | translate: { count: fulfillment().stopped }">{{
          fulfillment().stopped
        }}</guarantees-chip>
      }

      <guarantees-chip
        tone="neutral"
        variant="outline"
        icon="check_circle"
        [tooltip]="'guaranteeV2.message.fulfilledLinesHint' | translate: { done: fulfillment().fulfilled, total: summary().total }"
        >{{ fulfillment().fulfilled }}/{{ summary().total }}</guarantees-chip
      >
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionStatsComponent {
  summary = input.required<CoverageSummary>();
  fulfillment = input.required<FulfillmentSummary>();
}
